import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { 
  Scan, Play, AlertCircle, CheckCircle2, Clock,
  Database, TrendingUp, Fuel, ArrowRightLeft, 
  Wallet, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AssetConsolidationPage = () => {
  const [scans, setScans] = useState([]);
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  
  const [scanForm, setScanForm] = useState({
    network: 'polygon',
    asset_symbols: []
  });

  const networks = [
    { id: 'polygon', name: 'Polygon' },
    { id: 'ethereum', name: 'Ethereum' },
    { id: 'base', name: 'Base' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scansRes, batchesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/consolidation/scans`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/consolidation/batches`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/consolidation/stats`, { credentials: 'include' })
      ]);

      const scansData = await scansRes.json();
      const batchesData = await batchesRes.json();
      const statsData = await statsRes.json();

      setScans(scansData.scans || []);
      setBatches(batchesData.batches || []);
      setStats(statsData.stats || {});
    } catch (error) {
      toast.error('Failed to load consolidation data');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    setScanning(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/consolidation/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(scanForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Scan failed');
      }

      const data = await response.json();
      toast.success(`Scan complete! Found ${data.scan.wallets_with_assets} wallets with assets`);
      setShowScanModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setScanning(false);
    }
  };

  const handleExecute = async () => {
    if (selectedWallets.length === 0) {
      toast.error('Please select wallets to consolidate');
      return;
    }

    setExecuting(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/consolidation/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          scan_id: selectedScan.id,
          wallet_addresses: selectedWallets,
          asset_symbols: [], // All assets
          treasury_wallet_id: 'YOUR_TREASURY_ID', // TODO: Get from treasury selection
          auto_fund_gas: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Consolidation failed');
      }

      const data = await response.json();
      toast.success(`Consolidation complete! ${data.batch.assets_consolidated} assets consolidated`);
      setShowExecuteModal(false);
      setSelectedWallets([]);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExecuting(false);
    }
  };

  const toggleWalletSelection = (address) => {
    setSelectedWallets(prev =>
      prev.includes(address)
        ? prev.filter(a => a !== address)
        : [...prev, address]
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      completed_with_errors: 'bg-orange-100 text-orange-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Asset Consolidation</h1>
            <p className="text-muted-foreground">
              Scan user wallets and consolidate assets to treasury
            </p>
          </div>
          <Button onClick={() => setShowScanModal(true)}>
            <Scan className="h-4 w-4 mr-2" />
            New Scan
          </Button>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{stats.total_batches || 0}</div>
                <p className="text-sm text-muted-foreground">Total Batches</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">
                  {stats.total_wallets_consolidated || 0}
                </div>
                <p className="text-sm text-muted-foreground">Wallets Consolidated</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.total_assets_consolidated || 0}
                </div>
                <p className="text-sm text-muted-foreground">Assets Consolidated</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-purple-600">
                  {stats.total_gas_funded || 0}
                </div>
                <p className="text-sm text-muted-foreground">Gas Funded</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Scans */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Scans</CardTitle>
          </CardHeader>
          <CardContent>
            {scans.length === 0 ? (
              <div className="text-center py-12">
                <Scan className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No scans yet</p>
                <Button className="mt-4" onClick={() => setShowScanModal(true)}>
                  Create First Scan
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {scans.slice(0, 5).map((scan) => (
                  <div key={scan.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="h-5 w-5 text-blue-600" />
                          <span className="font-bold">{scan.network.toUpperCase()}</span>
                          <Badge className={getStatusColor(scan.status)}>{scan.status}</Badge>
                        </div>

                        <div className="grid grid-cols-4 gap-4 text-sm text-muted-foreground">
                          <div>
                            <strong>Scanned:</strong> {scan.wallets_scanned}
                          </div>
                          <div>
                            <strong>With Assets:</strong> {scan.wallets_with_assets}
                          </div>
                          <div>
                            <strong>Need Gas:</strong> {scan.wallets_needing_gas}
                          </div>
                          <div>
                            <strong>Assets Found:</strong> {scan.total_assets_found}
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground mt-2">
                          {new Date(scan.scan_timestamp).toLocaleString()}
                        </div>
                      </div>

                      {scan.status === 'pending' && scan.wallets_with_assets > 0 && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedScan(scan);
                            setShowExecuteModal(true);
                          }}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Consolidate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Batches */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Consolidation Batches</CardTitle>
          </CardHeader>
          <CardContent>
            {batches.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No consolidation batches yet
              </div>
            ) : (
              <div className="space-y-3">
                {batches.slice(0, 5).map((batch) => (
                  <div key={batch.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <ArrowRightLeft className="h-5 w-5 text-green-600" />
                          <span className="font-bold">{batch.network.toUpperCase()}</span>
                          <Badge className={getStatusColor(batch.status)}>{batch.status}</Badge>
                        </div>

                        <div className="grid grid-cols-4 gap-4 text-sm text-muted-foreground">
                          <div>
                            <strong>Wallets:</strong> {batch.wallets_processed}/{batch.wallets_total}
                          </div>
                          <div>
                            <strong>Assets:</strong> {batch.assets_consolidated}
                          </div>
                          <div>
                            <strong>Gas Funded:</strong> {batch.gas_funded_count}
                          </div>
                          <div>
                            <strong>Errors:</strong> {batch.errors?.length || 0}
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground mt-2">
                          Started: {new Date(batch.started_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scan Modal */}
        {showScanModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle>Scan User Wallets</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleScan} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Network *</Label>
                    <select
                      value={scanForm.network}
                      onChange={(e) => setScanForm({...scanForm, network: e.target.value})}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      {networks.map(net => (
                        <option key={net.id} value={net.id}>{net.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        This will scan all user blockchain wallets on {scanForm.network} and detect balances.
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowScanModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={scanning}>
                      {scanning ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Scan className="h-4 w-4 mr-2" />
                          Start Scan
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Execute Modal */}
        {showExecuteModal && selectedScan && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
            <Card className="max-w-4xl w-full mx-4 my-8">
              <CardHeader>
                <CardTitle>Execute Consolidation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <strong>Important:</strong> This will move assets from user wallets to treasury.
                      Internal balances will NOT change - only blockchain custody.
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Wallets to Consolidate</Label>
                  <div className="border rounded-lg max-h-96 overflow-y-auto">
                    {selectedScan.wallets?.filter(w => w.total_assets > 0).map((wallet) => (
                      <div
                        key={wallet.wallet_address}
                        className="p-3 border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleWalletSelection(wallet.wallet_address)}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedWallets.includes(wallet.wallet_address)}
                            onChange={() => {}}
                            className="rounded"
                          />
                          <div className="flex-1">
                            <div className="font-mono text-sm">{wallet.wallet_address}</div>
                            <div className="text-xs text-muted-foreground">
                              {wallet.user_email} • {wallet.total_assets} assets
                              {!wallet.has_sufficient_gas && (
                                <span className="text-orange-600"> • Needs gas</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedWallets.length} of {selectedScan.wallets_with_assets} wallets selected
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowExecuteModal(false);
                      setSelectedWallets([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleExecute} disabled={executing || selectedWallets.length === 0}>
                    {executing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Consolidating...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Execute Consolidation
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
